import { useState } from 'react'
import { NavLink, useNavigate } from 'react-router'
import type { User } from '../../lib/types.ts'
import { useLogout } from '../../hooks/useSession.ts'
import { SearchField } from '../../ui/SearchField.tsx'
import { NotificationBell } from './Notifications.tsx'
import { ConnectionBadge, PresenceStack } from './Presence.tsx'
import styles from './TopBar.module.css'

/**
 * docs/ui-spec.md §4. Notes sit behind a separator on purpose: the navigation has to say that they
 * are an independent module, not an annex to tasks.
 *
 */
export function TopBar({ user }: { user: User }): React.JSX.Element {
  const logout = useLogout()
  const navigate = useNavigate()
  const [globalQuery, setGlobalQuery] = useState('')

  return (
    <header className={styles.bar}>
      <div className={styles.brand}>
        <span className={styles.mark} aria-hidden="true" />
        <span className={styles.brandName}>ensynio</span>
      </div>

      <span className={styles.divider} aria-hidden="true" />

      <nav className={styles.tabs}>
        <Tab to="/">Start</Tab>
        <Tab to="/board">Board</Tab>
        <Tab to="/table">Tabela</Tab>
        <Tab to="/closed">Zamknięte</Tab>
        <span className={styles.divider} aria-hidden="true" />
        <Tab to="/notes">Notatki</Tab>
      </nav>

      <div className={styles.spacer} />

      <ConnectionBadge />

      <NotificationBell />

      {/* Enter przenosi do tabeli z ustawionym filtrem. Pole długo było atrapą — bez `value`,
          bez `onChange`, bez niczego — a skrót `/` i strona Pomocy twierdziły, że działa.
          Tabela ma już wyszukiwanie po stronie serwera, więc to jest wejście do niego,
          a nie druga implementacja tego samego. */}
      <SearchField
        scope="global"
        value={globalQuery}
        onChange={setGlobalQuery}
        onSubmit={(q) => {
          const trimmed = q.trim()
          if (trimmed) void navigate(`/table?q=${encodeURIComponent(trimmed)}`)
        }}
        placeholder="Szukaj"
        label="Szukaj w zadaniach"
        className={styles.search}
        showHint
      />

      <div className={styles.user}>
        <PresenceStack />
        <span className={styles.userName}>{user.displayName}</span>
      </div>

      <Tab to="/help">Pomoc</Tab>
      <Tab to="/settings">Ustawienia</Tab>

      <button
        className={styles.logout}
        type="button"
        onClick={() => logout.mutate()}
        disabled={logout.isPending}
      >
        Wyloguj
      </button>
    </header>
  )
}

function Tab({ to, children }: { to: string; children: string }): React.JSX.Element {
  return (
    <NavLink
      to={to}
      // `end` on "/" only: NavLink matches by prefix, so without it Start would light up on every
      // screen in the app. Every other tab has a path that cannot prefix another one.
      end={to === '/'}
      className={({ isActive }) => (isActive ? `${styles.tab} ${styles.tabActive}` : styles.tab)}
    >
      {children}
    </NavLink>
  )
}
